import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { coverBg, listPublishedCatalog, formatPrice } from "@/lib/courses";
import { BookOpen, Loader2 } from "lucide-react";

const LEVELS = ["all", "Beginner", "Intermediate", "Advanced"];

export default function PublicCatalog() {
  const { user } = useAuth();
  const homePath =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "teacher"
        ? "/teacher"
        : user?.role === "student"
          ? "/student"
          : "/";
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");

  useEffect(() => {
    setLoading(true);
    listPublishedCatalog({
      ...(level !== "all" ? { level } : {}),
      ...(q ? { q } : {}),
    })
      .then(setCourses)
      .finally(() => setLoading(false));
  }, [q, level]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="public-catalog">
      <DashboardHeader subtitle="Browse courses" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <BackButton to={homePath} label={user ? "Dashboard" : "Home"} />
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-amber-600" /> Course catalog
            </h1>
            <p className="text-sm text-stone-500">
              Published courses by our teachers. Enrolment is coming soon.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title…"
              className="w-56"
              data-testid="catalog-search"
            />
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-40" data-testid="catalog-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEVELS.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l === "all" ? "All levels" : l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-stone-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
          </div>
        ) : courses.length === 0 ? (
          <Card className="border-dashed border-stone-300">
            <CardContent className="py-16 text-center text-stone-500" data-testid="catalog-empty">
              No published courses match your filters.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-list">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/courses/${c.id}`}
                data-testid={`catalog-card-${c.id}`}
                className="block"
              >
                <Card className="border-stone-200 hover:border-stone-400 transition-colors h-full">
                  <div className={`h-24 ${coverBg(c.cover_color)} border-b`} />
                  <CardContent className="py-4 space-y-2">
                    <div className="font-semibold tracking-tight line-clamp-2">{c.title}</div>
                    {c.subtitle && (
                      <div className="text-xs text-stone-500 line-clamp-2">{c.subtitle}</div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
                      <Badge variant="outline">{c.level}</Badge>
                      <Badge variant="outline">{c.chapter_titles.length} ch</Badge>
                      <Badge variant="outline">{formatPrice(c.pricing)}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
